import { useEffect, useState } from 'react';
import { publicApi, type PublicRegisterInfo } from './api/client.js';

function when(iso: string | null, tz: string): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short', timeZone: tz }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

/**
 * Public sign-up page, reached through a short link (/j/CODE for students,
 * /s/CODE for speakers and mentors). No sign-in. The page asks for a name, an
 * email and a question or two, then registers the person with Zoom and hands
 * them their own join link.
 */
export function RegisterPage({ code }: { code: string }) {
  const [info, setInfo] = useState<PublicRegisterInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [firstName, setFirst] = useState('');
  const [lastName, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [roomId, setRoom] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ status: 'registered' | 'pending'; displayName: string; joinUrl?: string } | null>(null);

  useEffect(() => {
    void publicApi.info(code).then(setInfo).catch((e: unknown) => setLoadError(e instanceof Error ? e.message : 'This link is not valid.'));
  }, [code]);

  // Within half an hour of the start, send them straight into Zoom.
  useEffect(() => {
    if (!done?.joinUrl || !info?.startsAt) return;
    const lead = new Date(info.startsAt).getTime() - Date.now();
    if (lead < 30 * 60_000 && lead > -3 * 3600_000) {
      const t = window.setTimeout(() => window.location.assign(done.joinUrl!), 1500);
      return () => window.clearTimeout(t);
    }
  }, [done, info]);

  if (loadError) return <Shell title="Sign-up"><p role="alert">{loadError}</p></Shell>;
  if (!info) return <Shell title="Sign-up"><p className="muted">Loading…</p></Shell>;

  const speaker = info.kind === 'speaker';
  const heading = speaker ? `Speakers and mentors: ${info.title}` : info.title;
  const sub = info.startsAt ? when(info.startsAt, info.timezone) : '';

  if (done) {
    return (
      <Shell title={heading} sub={sub}>
        {done.status === 'pending' ? (
          <div className="banner banner-info" role="status">
            <strong>Thank you, {done.displayName}.</strong>
            <p>Your details have been received. The organiser will confirm your place, and Zoom will email you your personal link when they do.</p>
          </div>
        ) : (
          <div className="banner banner-info" role="status">
            <strong>You are registered, {done.displayName}.</strong>
            <p>Zoom has emailed you your personal link. Use it on the evening; it is only for you. You can also open it from here.</p>
            <p>
              <a className="btn" href={done.joinUrl}>Open my Zoom link</a>
            </p>
            <p className="muted">Please keep the name Zoom shows ({done.displayName}). We use it to put you in the right room.</p>
          </div>
        )}
      </Shell>
    );
  }
  if (!info.open) return <Shell title={heading} sub={sub}><p>Registration is not open yet. Please check back later or ask the organiser.</p></Shell>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDone(await publicApi.submit(code, { firstName, lastName, email, answers, roomId: speaker ? roomId : undefined, consent, website }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title={heading} sub={sub}>
      <form onSubmit={(e) => void submit(e)} className="form">
        <label htmlFor="fn">First name</label>
        <input id="fn" type="text" required maxLength={40} autoComplete="given-name" value={firstName} onChange={(e) => setFirst(e.target.value)} />
        <label htmlFor="ln">Last name</label>
        <input id="ln" type="text" required maxLength={40} autoComplete="family-name" value={lastName} onChange={(e) => setLast(e.target.value)} />
        <label htmlFor="em">Email</label>
        <input id="em" type="email" required maxLength={254} autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <span className="muted">Zoom sends your personal link here.</span>
        {speaker && (
          <>
            <label htmlFor="room">Which room will you be in?</label>
            <select id="room" required value={roomId} onChange={(e) => setRoom(e.target.value)}>
              <option value="">Choose…</option>
              {info.rooms.map((r) => (
                <option key={r.roomId} value={r.roomId}>{r.roomName}</option>
              ))}
            </select>
          </>
        )}
        {info.questions.map((q) => (
          <div key={q.id}>
            <label htmlFor={`q-${q.id}`}>{q.label}{q.required ? '' : ' (optional)'}</label>
            <input id={`q-${q.id}`} type="text" maxLength={200} required={q.required} value={answers[q.id] ?? ''} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          </div>
        ))}
        {/* Honeypot: people never see or fill this; scripts often do. */}
        <div className="hp" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input id="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>
        <label className="check">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required /> I agree that my name and email are used to run this evening, and deleted {info.retentionDays} days afterwards.
        </label>
        {error && <div className="banner banner-error" role="alert">{error}</div>}
        <button className="primary" type="submit" disabled={busy || !consent}>
          {busy ? 'Registering…' : speaker ? 'Send my details' : 'Register and get my link'}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div>
      <h1>{title}</h1>
      {sub && <p className="muted">{sub}</p>}
      {children}
    </div>
  );
}
